/**
 * The decisions behind dragging a row to reorder it, without React or the
 * DOM, so `test/reorder.test.ts` runs them under `node --test`. The hook that
 * listens to the pointer is `usePointerReorder.ts`.
 */

/** Long enough that a scroll flick is not a pick-up; the same 450ms `Row`'s
 *  own long press uses. */
export const HOLD_MS = 450

/** Movement that is a gesture rather than a tremble — `Row`'s own SLOP. */
export const SLOP = 12

/**
 * What a pointer that is down on a row and not yet dragging means, given how
 * far it has moved and whether it has been held still for `HOLD_MS`.
 *
 * A finger has to be held first: the same vertical movement unheld is the
 * page scrolling, and taking that from it would make every list a list you
 * cannot scroll. A mouse has no scroll to take, so a vertical drag picks the
 * row up straight away. A horizontal one is left to `Row`'s swipe either way.
 */
export function dragIntent(
  pointerType: string, dx: number, dy: number, held: boolean,
): 'wait' | 'drag' | 'cancel' {
  const moved = Math.abs(dx) > SLOP || Math.abs(dy) > SLOP
  if (held) return moved ? 'drag' : 'wait'
  if (!moved) return 'wait'
  if (pointerType === 'mouse' && Math.abs(dy) > Math.abs(dx)) return 'drag'
  return 'cancel'
}

/**
 * The order after dropping `dragId` on `overId`: the dragged row takes the
 * slot the target had, so dropping on the last row makes it last and on the
 * first makes it first. (Inserting *before* the target, which the HTML5
 * version did, meant a row could never be dragged to the bottom.) `null` when
 * nothing would change or either id is not in `ids`.
 */
export function moveTo(ids: string[], dragId: string, overId: string): string[] | null {
  const from = ids.indexOf(dragId)
  const to = ids.indexOf(overId)
  if (from < 0 || to < 0 || from === to) return null
  const next = ids.filter(id => id !== dragId)
  next.splice(to, 0, dragId)
  return next
}

/**
 * The order after a keyboard move (Alt+↑/↓, a3-29): `id` swaps places with
 * its neighbour in the group it is drawn in — an aisle, a template's section,
 * a task's subtasks — not with whatever line happens to sit next to it in
 * `all`, the list's whole position order, which may be in another aisle and
 * would make the key look like it did nothing. `null` at either end of the
 * group, or when `id` is in none of them.
 */
export function stepIn(all: string[], groups: readonly (readonly string[])[], id: string, dir: -1 | 1): string[] | null {
  const group = groups.find(g => g.includes(id))
  if (!group) return null
  const over = group[group.indexOf(id) + dir]
  return over === undefined ? null : moveTo(all, id, over)
}

/** Which side of a drop target the dragged row lands on. */
export type DropSide = 'before' | 'after'

/**
 * The order after dropping `dragId` on the `side` of `overId`. `moveTo` swaps
 * into the target's slot, which is right for a column of rows (dropping on the
 * last row makes it last) but cannot say "after the last chip" for a row you
 * are dragging along: the finger is on the target's left or right half, and
 * which half is the whole answer. `null` when the result is the order it
 * already had -- the hook uses that to draw no drop line where a drop would do
 * nothing, so a line always means something will move.
 */
export function moveRelative(ids: string[], dragId: string, overId: string, side: DropSide): string[] | null {
  if (dragId === overId || !ids.includes(dragId) || !ids.includes(overId)) return null
  const rest = ids.filter(id => id !== dragId)
  const at = rest.indexOf(overId) + (side === 'after' ? 1 : 0)
  const next = [...rest.slice(0, at), dragId, ...rest.slice(at)]
  return next.every((id, i) => id === ids[i]) ? null : next
}

/** `id` moved to the first or last place. `null` if it is already there or
 *  is not in `ids` -- the menu hides the item rather than offering a no-op. */
export function moveToEdge(ids: string[], id: string, edge: 'start' | 'end'): string[] | null {
  if (!ids.includes(id)) return null
  const rest = ids.filter(x => x !== id)
  const next = edge === 'start' ? [id, ...rest] : [...rest, id]
  return next.every((x, i) => x === ids[i]) ? null : next
}

/**
 * `all` (every id, in the order the database holds them) with the ids in
 * `reordered` rearranged into its order, in the slots they took up. Archived
 * lists are not chips, so a drag reorders the chips and must not shuffle the
 * archived ones' positions around them: each id outside `reordered` keeps its
 * place, each id inside takes the next one from `reordered`. Ids `reordered`
 * names that `all` lacks are dropped.
 */
export function fillSlots(all: string[], reordered: string[]): string[] {
  const inSet = new Set(reordered)
  const queue = reordered.filter(id => all.includes(id))
  let k = 0
  return all.map(id => (inSet.has(id) ? queue[k++] : id))
}
